import type { NuxtApp } from '#app'
import { useAuthStore } from '@/stores/auth'
import { ApolloClientProxy } from '@/utils/http'
import {
  ApolloClient,
  createHttpLink,
  type DefaultOptions,
  InMemoryCache,
} from '@apollo/client/core'
import merge from 'lodash/merge.js'

export default defineNuxtPlugin(({ $config, $token }: NuxtApp) => {
  const authStore = useAuthStore()

  const noCachingOptions: DefaultOptions = {
    watchQuery: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore',
    },
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  }

  /**
   * Link that attach authorisation headers if a token
   * exists
   */
  const httpLink = createHttpLink({
    uri: $config.public.graphqlEndpoint,
    fetch: async (uri: string | Request | URL, options: RequestInit) => {
      const authHeaders = await $token.getAuthorizationHeader()
      options = merge(options, authHeaders)
      options.headers['X-Source'] = 'Source'
      return fetch(uri, options)
    },
  })

  /**
   * This client is for direct WordPress queries when the main API fails
   */
  const wordpressDirectClient = new ApolloClient({
    cache: new InMemoryCache(),
    defaultOptions: noCachingOptions,
    uri: $config.public.graphqlEndpoint, // Using graphqlEndpoint as specified
    headers: { 'X-Source': 'Source-Direct' },
  })

  /**
   * This client is meant to be used with queries
   * that don't require authorisation, such as archive
   * pages
   */
  const apolloClient = new ApolloClientProxy(
    new ApolloClient({
      cache: new InMemoryCache(),
      defaultOptions: noCachingOptions,
      uri: $config.public.publicGraphqlEndpoint,
      headers: { 'X-Source': 'Source' },
    }),
    $token,
    { maxNumberOfRetries: 1 },
    wordpressDirectClient, // Pass the WordPress direct client
  )

  /**
   * This client is for queries that require authorisation,
   * such as the Membership article detail page
   */
  const privateApolloClient: ApolloClientProxy = new ApolloClientProxy(
    new ApolloClient({
      link: httpLink,
      cache: new InMemoryCache(),
      defaultOptions: noCachingOptions,
    }),
    $token,
    { maxNumberOfRetries: 1 },
    wordpressDirectClient, // Pass the WordPress direct client
  )

  const getGraphqlClient = () => {
    return authStore.isAuthenticated || $config.public.isPreviewSite
      ? privateApolloClient
      : apolloClient
  }

  return {
    provide: {
      apollo: {
        publicClient: apolloClient,
        privateClient: privateApolloClient,
        getClient: getGraphqlClient,
      },
    },
  }
})
